// Failed subscription payments via stripe.invoices.
// open + uncollectible invoices with attempt_count > 0 = payment was tried and failed.
// stripe.charges is not synced; decline detail comes from last_finalization_error on
// the invoice, which covers card declines surfaced at finalization time.
export const PAYMENT_FAILURES_SQL = `
  WITH customer_to_company AS (
    SELECT DISTINCT ON (cb.stripe_customer_id)
      cb.stripe_customer_id,
      comp.name AS company,
      comp.external_id AS company_external_id
    FROM public.company_billing cb
    JOIN public.companies comp ON comp.external_id = cb.company_external_id
    WHERE cb.stripe_customer_id IS NOT NULL
    ORDER BY cb.stripe_customer_id, cb.billing_date_utc DESC NULLS LAST
  ),
  company_user AS (
    SELECT DISTINCT ON (ur.company_external_id)
      ur.company_external_id,
      u.email AS user_email
    FROM public.user_roles ur
    JOIN public.users u ON u.external_id = ur.user_external_id
    WHERE ur.status = 'active'
      AND u.email NOT ILIKE '%@gomarloo.com'
    ORDER BY ur.company_external_id, u.created_at_utc ASC
  )
  SELECT
    i.id AS charge_id,
    TIMEZONE('UTC', TO_TIMESTAMP((i._raw_data->>'created')::bigint)) AS created_at_utc,
    (i._raw_data->>'amount_due')::numeric / 100.0 AS amount,
    UPPER(i._raw_data->>'currency') AS currency,
    false AS captured,
    (i._raw_data->>'amount_due')::numeric / 100.0 AS converted_amount,
    COALESCE(
      i._raw_data->'last_finalization_error'->>'code',
      i.status
    ) AS decline_reason,
    i._raw_data->>'description' AS description,
    COALESCE(
      i._raw_data->'last_finalization_error'->>'message',
      'Payment failed after ' || COALESCE(i._raw_data->>'attempt_count', '1') || ' attempt(s)'
    ) AS seller_message,
    COALESCE(i._raw_data->>'customer_email', cust._raw_data->>'email') AS customer_email,
    ctc.company,
    cu.user_email
  FROM stripe.invoices i
  LEFT JOIN stripe.customers cust ON cust.id = (i._raw_data->>'customer')
  LEFT JOIN customer_to_company ctc ON ctc.stripe_customer_id = (i._raw_data->>'customer')
  LEFT JOIN company_user cu ON cu.company_external_id = ctc.company_external_id
  WHERE i.status IN ('open', 'uncollectible')
    AND i.livemode = true
    AND (i._raw_data->>'billing_reason') IN ('subscription_cycle', 'subscription_create')
    AND COALESCE((i._raw_data->>'attempt_count')::int, 0) > 0
  ORDER BY (i._raw_data->>'created')::bigint DESC
`;

export interface FailedChargeRow {
  charge_id: string;
  created_at_utc: string;
  amount: number;
  currency: string;
  captured: boolean;
  converted_amount: number;
  decline_reason: string | null;
  description: string | null;
  seller_message: string | null;
  customer_email: string | null;
  company: string | null;
  user_email: string | null;
}

export function mapFailedChargeRows(columns: string[], rows: unknown[][]): FailedChargeRow[] {
  const idx: Record<string, number> = {};
  columns.forEach((c, i) => { idx[c] = i; });
  const get = (row: unknown[], key: string): unknown =>
    idx[key] !== undefined ? row[idx[key] as number] : undefined;
  return rows.map((r) => ({
    charge_id: String(get(r, "charge_id") ?? ""),
    created_at_utc: String(get(r, "created_at_utc") ?? ""),
    amount: Number(get(r, "amount") ?? 0),
    currency: String(get(r, "currency") ?? ""),
    captured: get(r, "captured") === true || String(get(r, "captured")).toLowerCase() === "true",
    converted_amount: Number(get(r, "converted_amount") ?? 0),
    decline_reason: (get(r, "decline_reason") as string | null) ?? null,
    description: (get(r, "description") as string | null) ?? null,
    seller_message: (get(r, "seller_message") as string | null) ?? null,
    customer_email: (get(r, "customer_email") as string | null) ?? null,
    company: (get(r, "company") as string | null) ?? null,
    user_email: (get(r, "user_email") as string | null) ?? null,
  }));
}
